import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/hooks/useOrganization';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  type PostgresRealtimeListener,
  useRealTimeSubscription,
} from '@/hooks/useRealTimeSubscription';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME } from '@/lib/queryCacheConfig';
import { fetchKitchenBoardTickets } from '@/hooks/kitchen/kitchenBoardCache';
import { hasItemsReadyToServe } from '@/services/kitchen/kitchenTicketStatus';
import { playOrderNotification } from '@/utils/playOrderNotification';
import type { KitchenTicket } from '@/types/kitchen';
import { FEATURE_FLAGS } from '@/types/featureFlags';

export function useKitchenReadyNotifications() {
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const { isEnabled, isLoading: isFlagsLoading } = useFeatureFlag();
  const orgId = currentOrganization?.id ?? '';
  const isFeatureEnabled = isEnabled(FEATURE_FLAGS.KITCHEN_VIEW);

  const ticketsKey = queryKeys.kitchenTickets(orgId);

  const {
    data: tickets = [],
    isPending: isLoading,
  } = useQuery<KitchenTicket[]>({
    queryKey: ticketsKey,
    queryFn: () => fetchKitchenBoardTickets(orgId),
    enabled: isFeatureEnabled && !!currentOrganization?.id,
    staleTime: STALE_TIME.kitchenTickets,
  });

  const readyTickets = useMemo(
    () => tickets.filter(hasItemsReadyToServe),
    [tickets],
  );
  const readyCount = readyTickets.length;
  const prevReadyCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isFeatureEnabled) return;
    if (isLoading) return;
    if (prevReadyCountRef.current === null) {
      prevReadyCountRef.current = readyCount;
      return;
    }
    if (readyCount > prevReadyCountRef.current) {
      void playOrderNotification();
    }
    prevReadyCountRef.current = readyCount;
  }, [readyCount, isLoading, isFeatureEnabled]);

  const canSubscribeToRealtime = isFeatureEnabled && !isFlagsLoading && !!orgId;

  const realtimeListeners: PostgresRealtimeListener[] = useMemo(() => {
    if (!canSubscribeToRealtime) return [];
    return [
      {
        config: {
          event: '*',
          schema: 'public',
          table: 'kitchen_tickets',
          filter: `organization_id=eq.${orgId}`,
        },
        invalidateKeys: [ticketsKey],
      },
      {
        config: {
          event: '*',
          schema: 'public',
          table: 'kitchen_ticket_items',
          filter: `organization_id=eq.${orgId}`,
        },
        invalidateKeys: [ticketsKey],
      },
    ];
  }, [orgId, canSubscribeToRealtime, ticketsKey]);

  useRealTimeSubscription({
    channelName: `kitchen-ready-${orgId}`,
    orgId: canSubscribeToRealtime ? orgId : undefined,
    queryClient,
    listeners: canSubscribeToRealtime ? realtimeListeners : [],
  });

  return {
    isFeatureEnabled,
    readyTickets,
    readyCount,
  };
}
